import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiService } from "@/services/api/ApiService";
import { AgentService, AgentQueryService } from "@/services/agent/core";
import { activityService } from "@/services/analytics/ActivityService";
import { Agent } from "@/types/agent/agent";
import type { AgentFilters } from "@/services/agent/core/AgentQueryService";
import type { Activity } from "@/services/analytics/ActivityService";

export const useAgents = (limit: number = 30, filters?: AgentFilters) => {
  return useInfiniteQuery({
    queryKey: ["agents", limit, filters],
    queryFn: ({ pageParam }) => AgentService.getAgents(pageParam, limit, false, undefined, filters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      allAgents: data.pages.flatMap((page) => page.data),
      total: data.pages[0]?.total || 0,
    }),
  });
};

export const useUserAgents = (limit: number = 30) => {
  return useQuery({
    queryKey: ["user-agents", limit],
    queryFn: () => AgentService.getAgents(undefined, limit),
    staleTime: 5 * 60 * 1000, // 5 minutes (matches CACHE_DURATION)
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => data.data || [],
  });
};

export const useGenieLimits = () => {
  return useQuery({
    queryKey: ["genie-limits"],
    queryFn: () => AgentQueryService.getLimits(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAgentsList = (searchName?: string, userId?: string) => {
  return useQuery({
    queryKey: ["agents-list", searchName, userId],
    queryFn: () => AgentService.list(searchName, userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => data || [],
  });
};

export const useAllAgents = (limit: number = 30, userId?: string, filters?: AgentFilters) => {
  return useInfiniteQuery({
    queryKey: ["all-agents", limit, userId, filters],
    queryFn: ({ pageParam }) => AgentService.getAgents(pageParam, limit, true, userId, filters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      allAgents: data.pages.flatMap((page) => page.data),
      total: data.pages[0]?.total || 0,
    }),
  });
};

export const useAgent = (agentId: string, options?: { initialData?: Agent }) => {
  return useQuery({
    queryKey: ["agent", agentId],
    queryFn: () => AgentService.getAgentDetails(agentId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!agentId,
    initialData: options?.initialData,
    // initialData (e.g. a genie card passed via router state) is a partial
    // snapshot from a lighter list query; mark it stale immediately so the
    // full detail fetch (with fields like document owners) always runs.
    initialDataUpdatedAt: options?.initialData ? 0 : undefined,
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agent,
      updateData,
    }: {
      agent: Agent;
      updateData: Partial<Agent> & Record<string, unknown>;
    }) => {
      console.log("Update data:", updateData);

      return await AgentService.update(agent, updateData);
    },
    onSuccess: (updatedAgent) => {
      // Update the individual agent cache immediately with returned data
      // so the UI reflects changes even if the refetch fails
      if (updatedAgent?.id) {
        queryClient.setQueryData(["agent", updatedAgent.id], updatedAgent);
      }

      queryClient.invalidateQueries({
        queryKey: ["agent"],
      });
      queryClient.invalidateQueries({
        queryKey: ["agents"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user-agents"],
      });
      queryClient.invalidateQueries({
        queryKey: ["all-agents"],
      });
    },
  });
};

export const useEnablePhoneGoals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agentId: string) => {
      const data = await ApiService.callEdge<{ success?: boolean; error?: string }>("handle-agent", {
        action: "enable-phone-goals",
        agentId,
      });
      if (!data?.success) throw new Error(data?.error || "Failed to turn on phone goals");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
  });
};

export const useFixSignatureContacts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agent }: { agent: Agent }) => {
      const branding = await AgentService.fixSignatureContacts(agent.id);
      return { ...agent, branding };
    },
    onSuccess: (healedAgent) => {
      // The signature-style endpoint already persisted this to the agents
      // row — sync the query cache too, so the fix doesn't look "lost" if
      // the modal closes without a Save Branding click, or a stale cached
      // genie re-renders elsewhere.
      if (healedAgent?.id) {
        queryClient.setQueryData(["agent", healedAgent.id], healedAgent);
      }

      queryClient.invalidateQueries({ queryKey: ["agent"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ agent }: { agent: Agent }) => {
      const agentId = agent.elevenlabs_id;
      const dbAgentId = agent.id;

      if (!dbAgentId) {
        throw new Error("No database agent ID found");
      }

      return await AgentService.deleteAgent(agentId, dbAgentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents"],
      });

      queryClient.invalidateQueries({
        queryKey: ["user-agents"],
      });

      queryClient.invalidateQueries({
        queryKey: ["all-agents"],
      });

      queryClient.invalidateQueries({
        queryKey: ["documents-infinite"],
      });

      queryClient.invalidateQueries({
        queryKey: ["documents"],
      });
    },
  });
};

export const useAgentReorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sort: Record<string, number>) => {
      await AgentService.reorder(sort);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      toast.success("Genies reordered successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reorder genies");
    },
  });
};

export const useOptimisticAgentReorder = () => {
  const queryClient = useQueryClient();
  const reorderMutation = useAgentReorder();

  const reorderAgents = (reorderedAgents: Agent[]) => {
    const sort: Record<string, number> = {};
    reorderedAgents.forEach((agent, index) => {
      sort[agent.id] = index;
    });

    // Deferred to the next frame so dnd-kit finishes its own drag-end DOM
    // cleanup before React commits the reordered list; committing both in
    // the same tick can cause a removeChild NotFoundError.
    requestAnimationFrame(() => {
      // Optimistically update the cache so the UI reflects the new order immediately
      queryClient.setQueriesData<any>(
        { queryKey: ["agents"] },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;
          const allAgents = oldData.pages.flatMap((p: any) => p.data);
          // Apply new positions to matching agents
          const updatedAgents = allAgents.map((agent: Agent) =>
            sort[agent.id] !== undefined ? { ...agent, position: sort[agent.id] } : agent,
          );
          // Sort by position (nulls last), then created_at desc
          updatedAgents.sort((a: Agent, b: Agent) => {
            const posA = a.position ?? Number.MAX_SAFE_INTEGER;
            const posB = b.position ?? Number.MAX_SAFE_INTEGER;
            if (posA !== posB) return posA - posB;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          // Rebuild pages structure with all agents in first page
          return {
            ...oldData,
            pages: [{ ...oldData.pages[0], data: updatedAgents }, ...oldData.pages.slice(1).map((p: any) => ({ ...p, data: [] }))],
          };
        },
      );

      reorderMutation.mutate(sort);
    });
  };

  return {
    reorderAgents,
    isReordering: reorderMutation.isPending,
  };
};

export const useMarketplaceGenies = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["marketplace-genies"],
    queryFn: () => AgentService.listMarketplaceGenies(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => data || [],
    enabled,
  });
};

export const useUpdateAgentUrlName = (onHistoryUpdated?: (history: Activity[]) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agent, urlName }: { agent: Agent; urlName: string }) =>
      AgentService.update(agent, { url_name: urlName }),
    onSuccess: async (_, { agent, urlName }) => {
      queryClient.invalidateQueries({ queryKey: ["agent", agent.id] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });

      if (onHistoryUpdated) {
        await activityService.logQrUrlChanged(agent.name, agent.id, agent.url_name ?? "", urlName);
        const history = await activityService.getQrUrlChangeHistory(agent.id, 10);
        onHistoryUpdated(history);
      }
    },
  });
};

export const useElDetails = (agentId: string | null) => {
  return useQuery({
    queryKey: ["elevenlabs-agent", agentId],
    queryFn: async () => {
      const data = await ApiService.callEdge<any>("elevenlabs", {
        resource: "agents",
        action: "get",
        params: {
          entityId: agentId!,
        },
      });
      return data;
    },
    enabled: !!agentId,
  });
};
